import { useState, useEffect, useRef } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { ClipboardList, ArrowUp, ArrowDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { useRounds, useRoundDetail, useIssueDashboard } from '../hooks/useProjectTestResults';
import Spinner from '../components/ui/Spinner';
import SingleSelectDropdown from '../components/common/SingleSelectDropdown';
import { usePanelContext } from '../contexts/PanelContext';
import { useAuthStore } from '../store/authStore';
import type { ProjectDetail } from '../services/projectService';
import type { ProjectRoundSpecDetail, FailIssue, IssueDashboard, IssueDashboardAssignee } from '@maios/shared';
import { getContrastText } from '../components/issue/IssueListView';

// --- Constants ---

const PRIORITY_LABEL: Record<string, string> = {
  BLOCKER: '블로커',
  CRITICAL: '긴급',
  HIGH: '높음',
  MEDIUM: '보통',
  LOW: '낮음',
};

const PRIORITY_COLOR: Record<string, string> = {
  BLOCKER: 'bg-red-100 text-red-800',
  CRITICAL: 'bg-orange-100 text-orange-800',
  HIGH: 'bg-amber-100 text-amber-800',
  MEDIUM: 'bg-blue-100 text-blue-700',
  LOW: 'bg-gray-100 text-gray-600',
};

// --- Helpers ---

function pct(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}

function getRunStatusLabel(spec: ProjectRoundSpecDetail): string {
  if (!spec.runId) return '미참여';
  if (spec.runStatus === 'COMPLETED') return '완료';
  if (spec.runStatus === 'ABORTED') return '중단';
  return '진행중';
}

function getRunStatusColor(spec: ProjectRoundSpecDetail): string {
  if (!spec.runId) return 'text-gray-400';
  if (spec.runStatus === 'COMPLETED') return 'text-green-600';
  if (spec.runStatus === 'ABORTED') return 'text-gray-500';
  return 'text-amber-600';
}

// --- Main Page (standalone route wrapper) ---

export default function ProjectTestResultsPage() {
  const { project } = useOutletContext<{ project: ProjectDetail }>();
  return <ProjectTestResultsContent projectId={project.id} projectKey={project.projectKey} />;
}

// --- Reusable Content (REQ-V32-002: 서브탭 임베드용) ---

export function ProjectTestResultsContent({ projectId, projectKey }: { projectId: string; projectKey: string }) {
  // 회차 목록 조회
  const { data: rounds, isLoading: isLoadingRounds } = useRounds(projectId);
  const [selectedRound, setSelectedRound] = useState(0);

  // 최신 회차 기본 선택
  useEffect(() => {
    if (!rounds || rounds.length === 0) {
      setSelectedRound(0);
      return;
    }
    if (selectedRound === 0 || !rounds.some((r) => r.roundNumber === selectedRound)) {
      setSelectedRound(rounds[0].roundNumber);
    }
  }, [rounds, selectedRound]);

  // 선택된 회차 상세
  const { data: detail, isLoading: isLoadingDetail } = useRoundDetail(
    projectId,
    selectedRound,
  );

  // 이슈 대시보드 (회차 무관, 프로젝트 전체)
  const { data: dashboard } = useIssueDashboard(projectId);

  // 내보내기
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // 드롭다운 외부 클릭 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleExport(format: 'pdf' | 'markdown') {
    setShowExportMenu(false);
    setExporting(true);

    try {
      const { accessToken } = useAuthStore.getState();
      const response = await fetch(
        `/api/v1/projects/${projectId}/test-results/rounds/${selectedRound}/export?format=${format}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      if (!response.ok) throw new Error('Export failed');

      // Content-Disposition에서 파일명 추출
      const disposition = response.headers.get('Content-Disposition');
      let filename = `export.${format === 'pdf' ? 'pdf' : 'md'}`;
      if (disposition) {
        const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?(.+)/);
        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      // Blob 다운로드
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      toast.success(`${format === 'pdf' ? 'PDF' : '마크다운'} 파일이 다운로드됩니다.`);
    } catch {
      toast.error('내보내기에 실패했습니다.');
    } finally {
      setExporting(false);
    }
  }

  // --- Loading ---

  if (isLoadingRounds) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    );
  }

  const hasRounds = rounds && rounds.length > 0;

  return (
    <div>
      {/* Header + 회차 선택 */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">전체 테스트 결과</h2>
        {hasRounds && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">회차:</label>
              <SingleSelectDropdown
                label="회차 선택"
                value={String(selectedRound)}
                onChange={(v) => setSelectedRound(Number(v))}
                clearable={false}
                options={rounds!.map((r) => ({
                  value: String(r.roundNumber),
                  label: `${r.roundNumber}회차 (${r.specCount}개 명세 참여${r.status === 'IN_PROGRESS' ? ' - 진행 중' : ''})`,
                }))}
              />
            </div>

            {/* 내보내기 버튼 */}
            <div className="relative inline-block" ref={exportMenuRef}>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={exporting}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-50"
              >
                📄 내보내기
                <span className="text-sm">▼</span>
              </button>

              {showExportMenu && (
                <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <button
                    onClick={() => handleExport('pdf')}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 text-sm rounded-t-lg"
                  >
                    📕 PDF로 내보내기
                  </button>
                  <button
                    onClick={() => handleExport('markdown')}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 text-sm border-t border-gray-100 rounded-b-lg"
                  >
                    📝 마크다운(.md)으로 내보내기
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 내보내기 로딩 오버레이 */}
      {exporting && (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg px-6 py-4 shadow-lg flex items-center gap-3">
            <Spinner className="size-5" /> <span className="text-sm">내보내기 중...</span>
          </div>
        </div>
      )}

      {/* 빈 상태 */}
      {!hasRounds && (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <ClipboardList className="size-12 text-gray-300" />
          <p className="text-sm text-gray-500">
            실행 된 또는 실행 중인 테스트가 없습니다.
          </p>
        </div>
      )}

      {/* 로딩 */}
      {hasRounds && isLoadingDetail && (
        <div className="flex items-center justify-center h-64">
          <Spinner className="size-8" />
        </div>
      )}

      {/* 대시보드 */}
      {hasRounds && detail && (
        <RoundDashboard detail={detail} projectKey={projectKey} dashboard={dashboard ?? null} />
      )}
    </div>
  );
}

// --- Dashboard ---

function RoundDashboard({
  detail,
  projectKey,
  dashboard,
}: {
  detail: {
    roundNumber: number;
    specs: ProjectRoundSpecDetail[];
    totals: {
      totalCount: number;
      passCount: number;
      failCount: number;
      blockedCount: number;
      skipCount: number;
      notRunCount: number;
    };
    failIssues: FailIssue[];
    unlinkedFailTCCount: number;
    previousComparison: {
      previousRound: number;
      passDiff: number;
      failDiff: number;
    } | null;
  };
  projectKey: string;
  dashboard: IssueDashboard | null;
}) {
  const { totals, specs, failIssues, unlinkedFailTCCount, previousComparison } = detail;
  const { openDetailPanel } = usePanelContext();
  const { totalCount, passCount, failCount, blockedCount, skipCount, notRunCount } = totals;

  const passRate = pct(passCount, totalCount);
  const failRate = pct(failCount, totalCount);
  const blockedRate = pct(blockedCount, totalCount);
  const skipRate = pct(skipCount, totalCount);
  const notRunRate = pct(notRunCount, totalCount);

  // 참여 명세와 미참여 명세 분리 (참여 먼저, 미참여 뒤에)
  const participatingSpecs = specs.filter((s) => s.runId !== null);
  const nonParticipatingSpecs = specs.filter((s) => s.runId === null);
  const orderedSpecs = [...participatingSpecs, ...nonParticipatingSpecs];

  return (
    <div className="space-y-5">
      {/* A) 실행 현황 바 */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="h-4 bg-gray-100 rounded-full overflow-hidden flex mb-3">
          {passCount > 0 && (
            <div
              className="h-full bg-green-500"
              style={{ width: `${passRate}%` }}
              title={`PASS ${passRate}%`}
            />
          )}
          {failCount > 0 && (
            <div
              className="h-full bg-red-500"
              style={{ width: `${failRate}%` }}
              title={`FAIL ${failRate}%`}
            />
          )}
          {blockedCount > 0 && (
            <div
              className="h-full bg-amber-400"
              style={{ width: `${blockedRate}%` }}
              title={`BLOCKED ${blockedRate}%`}
            />
          )}
          {skipCount > 0 && (
            <div
              className="h-full bg-purple-400"
              style={{ width: `${skipRate}%` }}
              title={`SKIP ${skipRate}%`}
            />
          )}
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-green-500" />
            PASS {passCount}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500" />
            FAIL {failCount}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-amber-400" />
            BLOCKED {blockedCount}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-purple-400" />
            SKIP {skipCount}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-gray-300" />
            NOT_RUN {notRunCount}
          </span>
        </div>
      </div>

      {/* B) 요약 카드 */}
      <div className="grid grid-cols-6 gap-4">
        <SummaryCard title="총 TC" value={totalCount} />
        <SummaryCard
          title="PASS"
          value={passCount}
          rate={passRate}
          color="text-green-600"
        />
        <SummaryCard
          title="FAIL"
          value={failCount}
          rate={failRate}
          color="text-red-600"
        />
        <SummaryCard
          title="BLOCKED"
          value={blockedCount}
          rate={blockedRate}
          color="text-amber-600"
        />
        <SummaryCard
          title="SKIP"
          value={skipCount}
          rate={skipRate}
          color="text-purple-600"
        />
        <SummaryCard
          title="미실행"
          value={notRunCount}
          rate={notRunRate}
          color="text-gray-500"
        />
      </div>

      {/* C) 이전 회차 대비 */}
      {previousComparison && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-sm font-medium text-gray-700 mb-2">
            이전 회차({previousComparison.previousRound}회차) 대비:
          </div>
          <div className="flex items-center gap-6 text-sm">
            <span className="flex items-center gap-1">
              PASS:{' '}
              <span
                className={`inline-flex items-center gap-0.5 font-medium ${
                  previousComparison.passDiff >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                ({previousComparison.passDiff >= 0 ? '+' : ''}
                {previousComparison.passDiff})
                {previousComparison.passDiff > 0 ? (
                  <ArrowUp className="size-3.5" />
                ) : previousComparison.passDiff < 0 ? (
                  <ArrowDown className="size-3.5" />
                ) : null}
              </span>
            </span>
            <span className="flex items-center gap-1">
              FAIL:{' '}
              <span
                className={`inline-flex items-center gap-0.5 font-medium ${
                  previousComparison.failDiff <= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                ({previousComparison.failDiff >= 0 ? '+' : ''}
                {previousComparison.failDiff})
                {previousComparison.failDiff > 0 ? (
                  <ArrowUp className="size-3.5" />
                ) : previousComparison.failDiff < 0 ? (
                  <ArrowDown className="size-3.5" />
                ) : null}
              </span>
            </span>
          </div>
        </div>
      )}

      {/* D) 명세별 현황 테이블 */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">명세별 현황</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-32">
                  명세 코드
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  명세 제목
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider w-16">
                  PASS
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider w-16">
                  FAIL
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider w-16">
                  BLOCKED
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider w-16">
                  SKIP
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider w-16">
                  미실행
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider w-20">
                  진행률
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider w-20">
                  상태
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orderedSpecs.map((spec) => {
                const isParticipating = spec.runId !== null;
                const progress = isParticipating
                  ? pct(spec.totalCount - spec.notRunCount, spec.totalCount)
                  : 0;

                return (
                  <tr
                    key={spec.specId}
                    className={`hover:bg-gray-50 transition-colors ${
                      !isParticipating ? 'text-gray-400' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-mono text-sm whitespace-nowrap">
                      {isParticipating ? (
                        <Link
                          to={`/projects/${projectKey}/specs/${spec.specId}/test-results`}
                          className="text-primary-600 hover:text-primary-700 hover:underline"
                        >
                          {spec.specCode}
                        </Link>
                      ) : (
                        spec.specCode
                      )}
                    </td>
                    <td className="px-4 py-3">{spec.specTitle}</td>
                    <td className="px-4 py-3 text-center">
                      {isParticipating ? (
                        <span className="text-green-600 font-medium">{spec.passCount}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isParticipating ? (
                        <span className={spec.failCount > 0 ? 'text-red-600 font-medium' : ''}>
                          {spec.failCount}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isParticipating ? (
                        <span className={spec.blockedCount > 0 ? 'text-amber-600 font-medium' : ''}>
                          {spec.blockedCount}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isParticipating ? (
                        <span className={spec.skipCount > 0 ? 'text-purple-600 font-medium' : ''}>
                          {spec.skipCount}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isParticipating ? spec.notRunCount : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isParticipating ? `${progress}%` : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-sm font-medium ${getRunStatusColor(spec)}`}>
                        {getRunStatusLabel(spec)}
                      </span>
                    </td>
                  </tr>
                );
              })}

              {/* 합계 행 */}
              <tr className="bg-gray-50 font-medium border-t border-gray-200">
                <td className="px-4 py-3 text-sm text-gray-600">합계</td>
                <td className="px-4 py-3" />
                <td className="px-4 py-3 text-center text-green-600">{totalCount > 0 ? passCount : '-'}</td>
                <td className="px-4 py-3 text-center text-red-600">{totalCount > 0 ? failCount : '-'}</td>
                <td className="px-4 py-3 text-center text-amber-600">{totalCount > 0 ? blockedCount : '-'}</td>
                <td className="px-4 py-3 text-center text-purple-600">{totalCount > 0 ? skipCount : '-'}</td>
                <td className="px-4 py-3 text-center">{totalCount > 0 ? notRunCount : '-'}</td>
                <td className="px-4 py-3 text-center">
                  {totalCount > 0 ? `${pct(totalCount - notRunCount, totalCount)}%` : '-'}
                </td>
                <td className="px-4 py-3" />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* E) 이슈 현황 대시보드 */}
      {dashboard && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">작업 현황</h3>
          {dashboard.totalCount === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
              등록된 작업이 없습니다.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatusChart byStatus={dashboard.byStatus} />
              <PriorityChart byPriority={dashboard.byPriority} />
              <AssigneeTable byAssignee={dashboard.byAssignee} />
              <UnresolvedChart unresolvedByPriority={dashboard.unresolvedByPriority} />
            </div>
          )}
        </div>
      )}

      {/* F) FAIL 이슈 목록 */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">
            FAIL 작업 목록{' '}
            <span className="text-gray-400 font-normal">({failIssues.length})</span>
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-32">
                  작업 ID
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  제목
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-20">
                  우선순위
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-28">
                  상태
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-24">
                  담당자
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-32">
                  관련 TC
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {failIssues.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    이 회차에 등록된 FAIL 작업이 없습니다.
                  </td>
                </tr>
              ) : (
                failIssues.map((issue) => (
                  <tr key={issue.issueId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-sm text-primary-600 whitespace-nowrap">
                      {issue.issueId}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openDetailPanel('issue', issue.id)}
                        className="text-gray-900 hover:text-primary-600 hover:underline text-left"
                      >
                        {issue.title}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${PRIORITY_COLOR[issue.priority] || 'bg-gray-100 text-gray-600'}`}
                      >
                        {PRIORITY_LABEL[issue.priority] || issue.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium"
                        style={{
                          backgroundColor: issue.statusColor + '22',
                          color: getContrastText(issue.statusColor) === '#FFFFFF' ? issue.statusColor : getContrastText(issue.statusColor),
                        }}
                      >
                        {issue.statusName}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {issue.assignee ? issue.assignee.name : <span className="text-gray-400">미배정</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {issue.relatedTCs.map((tc) => (
                          <span
                            key={tc.testCaseId}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600"
                            title={tc.specCode}
                          >
                            {tc.tcCode}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 이슈 미연결 FAIL TC 경고 */}
      {unlinkedFailTCCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-amber-800">
          <span>⚠</span>
          <span>작업이 연결되지 않은 FAIL TC {unlinkedFailTCCount}건</span>
        </div>
      )}

    </div>
  );
}

// --- Sub-components ---

function SummaryCard({
  title,
  value,
  rate,
  color,
}: {
  title: string;
  value: number;
  rate?: number;
  color?: string;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
      <div className="text-sm font-medium text-gray-500 mb-1">{title}</div>
      <div className={`text-2xl font-bold ${color || 'text-gray-900'}`}>{value}</div>
      {rate !== undefined && (
        <div className="text-sm text-gray-400 mt-0.5">{rate}%</div>
      )}
    </div>
  );
}

// --- 이슈 대시보드 차트 컴포넌트 ---

// CHG-041: StatusChart에서 사용하는 기본 팔레트 (동적 워크플로우 상태 수에 맞게 순환)
const STATUS_PALETTE = ['#6B7280', '#3B82F6', '#8B5CF6', '#10B981', '#9CA3AF', '#F59E0B', '#EF4444'];

const DASHBOARD_PRIORITY_CONFIG: Array<{ key: string; label: string; barBg: string }> = [
  { key: 'BLOCKER', label: 'BLOCKER', barBg: 'bg-red-600' },
  { key: 'CRITICAL', label: 'CRITICAL', barBg: 'bg-red-400' },
  { key: 'HIGH', label: 'HIGH', barBg: 'bg-orange-400' },
  { key: 'MEDIUM', label: 'MEDIUM', barBg: 'bg-yellow-400' },
  { key: 'LOW', label: 'LOW', barBg: 'bg-gray-400' },
];

function StatusChart({ byStatus }: { byStatus: Record<string, number> }) {
  const total = Object.values(byStatus).reduce((a, b) => a + b, 0);
  const entries = Object.entries(byStatus);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-3">상태별 현황</h4>
      <div className="flex flex-wrap gap-2 mb-3">
        {entries.map(([name, count], idx) => (
          <div
            key={name}
            className="rounded-lg px-3 py-2 text-center min-w-[60px]"
            style={{ backgroundColor: STATUS_PALETTE[idx % STATUS_PALETTE.length] + '22' }}
          >
            <div className="text-[11px] text-gray-600">{name}</div>
            <div className="text-lg font-bold text-gray-900">{count}</div>
          </div>
        ))}
      </div>
      {total > 0 && (
        <div className="h-6 bg-gray-100 rounded-full overflow-hidden flex">
          {entries.map(([name, count], idx) => {
            if (count === 0) return null;
            const widthPct = (count / total) * 100;
            return (
              <div
                key={name}
                className="h-full"
                style={{
                  width: `${widthPct}%`,
                  backgroundColor: STATUS_PALETTE[idx % STATUS_PALETTE.length],
                }}
                title={`${name} ${count} (${Math.round(widthPct)}%)`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function PriorityChart({ byPriority }: { byPriority: Record<string, number> }) {
  const total = Object.values(byPriority).reduce((a, b) => a + b, 0);
  const maxCount = Math.max(...Object.values(byPriority), 1);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-3">우선순위별 현황</h4>
      <div className="space-y-2">
        {DASHBOARD_PRIORITY_CONFIG.map(({ key, label, barBg }) => {
          const count = byPriority[key] || 0;
          const widthPct = (count / maxCount) * 100;
          const ratePct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={key} className="flex items-center gap-2">
              <span className="text-sm text-gray-600 w-16 flex-shrink-0">{label}</span>
              <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                {count > 0 && (
                  <div
                    className={`h-full ${barBg} rounded`}
                    style={{ width: `${widthPct}%` }}
                  />
                )}
              </div>
              <span className="text-sm text-gray-700 w-16 flex-shrink-0 text-right">
                {count} ({ratePct}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AssigneeTable({ byAssignee }: { byAssignee: IssueDashboardAssignee[] }) {
  const totals = byAssignee.reduce(
    (acc, a) => ({
      total: acc.total + a.total,
      todo: acc.todo + (a.byCategory['TODO'] ?? 0),
      inProgress: acc.inProgress + (a.byCategory['IN_PROGRESS'] ?? 0),
      done: acc.done + (a.byCategory['DONE'] ?? 0),
      unresolved: acc.unresolved + a.unresolved,
    }),
    { total: 0, todo: 0, inProgress: 0, done: 0, unresolved: 0 },
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-3">담당자별 현황</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="py-2 text-left text-gray-600 font-semibold">담당자</th>
              <th className="py-2 text-center text-gray-600 font-semibold w-12">전체</th>
              <th className="py-2 text-center text-gray-600 font-semibold w-12">TODO</th>
              <th className="py-2 text-center text-gray-600 font-semibold w-12">진행중</th>
              <th className="py-2 text-center text-gray-600 font-semibold w-12">완료</th>
              <th className="py-2 text-center text-gray-600 font-semibold w-12">미해결</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {byAssignee.map((a) => (
              <tr key={a.userId ?? '__unassigned__'}>
                <td className={`py-2 ${a.userId === null ? 'text-gray-400 italic' : 'text-gray-900'}`}>
                  {a.name}
                </td>
                <td className="py-2 text-center text-gray-700">{a.total}</td>
                <td className="py-2 text-center text-gray-700">{a.byCategory['TODO'] ?? 0}</td>
                <td className="py-2 text-center text-gray-700">{a.byCategory['IN_PROGRESS'] ?? 0}</td>
                <td className="py-2 text-center text-gray-700">{a.byCategory['DONE'] ?? 0}</td>
                <td className={`py-2 text-center ${a.unresolved > 0 ? 'font-bold text-red-600' : 'text-gray-700'}`}>
                  {a.unresolved}
                </td>
              </tr>
            ))}
            {byAssignee.length > 0 && (
              <tr className="bg-gray-50 font-bold">
                <td className="py-2 text-gray-600">합계</td>
                <td className="py-2 text-center text-gray-700">{totals.total}</td>
                <td className="py-2 text-center text-gray-700">{totals.todo}</td>
                <td className="py-2 text-center text-gray-700">{totals.inProgress}</td>
                <td className="py-2 text-center text-gray-700">{totals.done}</td>
                <td className={`py-2 text-center ${totals.unresolved > 0 ? 'text-red-600' : 'text-gray-700'}`}>
                  {totals.unresolved}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UnresolvedChart({
  unresolvedByPriority,
}: {
  unresolvedByPriority: IssueDashboard['unresolvedByPriority'];
}) {
  const maxCount = Math.max(
    unresolvedByPriority.BLOCKER,
    unresolvedByPriority.CRITICAL,
    unresolvedByPriority.HIGH,
    unresolvedByPriority.MEDIUM,
    unresolvedByPriority.LOW,
    1,
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-1">미해결 작업 우선순위</h4>
      <div className="text-sm text-gray-500 mb-3">
        총 미해결: <span className="font-semibold text-gray-900">{unresolvedByPriority.total}</span>건
      </div>
      <div className="space-y-2">
        {DASHBOARD_PRIORITY_CONFIG.map(({ key, label, barBg }) => {
          const count = unresolvedByPriority[key as keyof typeof unresolvedByPriority] as number;
          const widthPct = (count / maxCount) * 100;
          return (
            <div key={key} className="flex items-center gap-2">
              <span className="text-sm text-gray-600 w-16 flex-shrink-0">{label}</span>
              <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                {count > 0 && (
                  <div
                    className={`h-full ${barBg} rounded`}
                    style={{ width: `${widthPct}%` }}
                  />
                )}
              </div>
              <span className="text-sm text-gray-700 w-8 flex-shrink-0 text-right">{count}</span>
            </div>
          );
        })}
      </div>
      {unresolvedByPriority.blockerCriticalCount > 0 && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded px-3 py-2 text-red-700 text-sm">
          ⚠ BLOCKER/CRITICAL 미해결: {unresolvedByPriority.blockerCriticalCount}건
        </div>
      )}
    </div>
  );
}
